import React, { useCallback, useEffect, useState } from 'react';
/*
 * `formatBRL`, e não um `toLocaleString` local — a regra da casa é que só
 * `utils/format.ts` sabe escrever dinheiro. Ela nasceu de nove `toFixed(1)`
 * espalhados que escreviam ponto onde o resto escrevia vírgula.
 *
 * Sem máscara: esta tela não tem o olhinho de "Ocultar valores", e o que ela
 * mostra é o total do GRUPO — nada da conta de quem está olhando.
 */
import { formatBRL } from '../utils/format';
import { SESSAO_FALSA_ATIVA } from '../dev/sessaoFalsa';
import { FRASE_DA_MARCA } from '../utils/marca';
import './ConvitePublico.css';

/**
 * As telas de quem foi convidado — sem sessão, sem app, sem cadastro.
 *
 * ── Por que não usa `services/api.ts` ────────────────────────────────────
 *
 * Aquele cliente tem um interceptor que busca a sessão do Supabase e põe um
 * `Authorization` em toda chamada. Aqui não há sessão, e não deve haver: quem
 * chega é alguém que talvez nunca tenha ouvido falar do app. `fetch` puro
 * deixa isso explícito em vez de depender de o interceptor não achar token.
 *
 * ── O peso dos dois botões é igual, e é de propósito ─────────────────────
 *
 * Um "recusar" pequeno, cinza, embaixo, é o padrão escuro clássico — e
 * transformaria este fluxo num consentimento fabricado. Se recusar é mais
 * difícil que aceitar, o "sim" não quer dizer nada.
 */
const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

/**
 * A sessão de desenvolvimento também alcança estas telas.
 *
 * ── Por que precisou de um caminho próprio ───────────────────────────────
 *
 * `services/api.ts` intercepta trocando o `adapter` do axios. Este arquivo não
 * usa axios de propósito — quem chega aqui não tem sessão, e o interceptor do
 * cliente põe um `Authorization` em toda chamada. O `fetch` puro escapava da
 * sessão falsa junto com o resto.
 *
 * O efeito era que a ÚNICA tela do app que alguém de fora vê era a única que
 * não dava para olhar rodando sem subir o backend — e o backend local aponta
 * para PRODUÇÃO (armadilha 1). Ou seja: a tela mais exposta do produto era a
 * menos conferível.
 *
 * Em produção `SESSAO_FALSA_ATIVA` é constante falsa e este bloco sai da build.
 */
const buscar = async (caminho: string, init?: RequestInit): Promise<Response> => {
  if (SESSAO_FALSA_ATIVA) {
    const rota = caminho.replace(`${API}/`, '');
    // Sob demanda: nenhum arquivo importa os dados fictícios de forma estática.
    const { respostaDeMentira } = await import('../demo/dados');
    const corpo = respostaDeMentira(init?.method ?? 'get', rota);
    return new Response(JSON.stringify(corpo ?? {}), {
      status: corpo === undefined ? 404 : 200,
      headers: { 'Content-Type': 'application/json' },
    });
  }
  return fetch(caminho, init);
};

/** O alvo de uma vaquinha é número redondo; centavos aqui são ruído. */
const SEM_CENTAVOS = { maximumFractionDigits: 0 } as const;

interface TextoDoEscopo { titulo: string; inclui: string; naoInclui: string }

interface Convite {
  donoNome: string;
  donoEmail: string;
  convidado: string;
  escopo: 'resumo' | 'completo';
  texto: TextoDoEscopo;
  status: string;
  podeDecidir: boolean;
  motivo: string | null;
}

interface Saida {
  donoNome: string;
  convidado: string;
  status: string;
  podeSair: boolean;
  motivo: string | null;
}

/**
 * A marca, montada como no cabeçalho do app: ícone em cima, nome, frase.
 *
 * ── Por que aqui importa mais que em qualquer outra tela ─────────────────
 *
 * Esta é a única tela do app que alguém de FORA vê, e quase sempre é o
 * primeiro contato dessa pessoa com o produto. Só o nome não diz o que ele é;
 * a frase diz, e é ela que transforma "recebi um email estranho" em "entendi
 * do que se trata".
 *
 * `FRASE_DA_MARCA` importada, nunca digitada: a frase já esteve em cinco
 * lugares e mudou em quatro. `aria-hidden` no ícone porque o nome logo abaixo
 * já diz FinTracking — a mesma decisão do `App.tsx`.
 */
const Moldura: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="convite-fundo">
    <div className="convite-caixa">
      <div className="convite-cabecalho">
        <img className="convite-icone" src={`${process.env.PUBLIC_URL}/icons/favicon.svg`}
             alt="" aria-hidden="true" width={56} height={56} />
        <h1 className="convite-marca"><span>FIN</span>TRACKING</h1>
        <p className="convite-frase">{FRASE_DA_MARCA}</p>
      </div>
      {children}
    </div>
  </div>
);

const Carregando: React.FC = () => (
  <Moldura><p className="convite-neutro">Carregando…</p></Moldura>
);

const Recado: React.FC<{ titulo: string; texto: string; tom?: 'bom' | 'neutro' }> =
  ({ titulo, texto, tom = 'neutro' }) => (
    <Moldura>
      <h2 className="convite-titulo">{titulo}</h2>
      <p className={`convite-recado ${tom}`}>{texto}</p>
      <a className="convite-link" href="https://fintracking.com.br">
        Conhecer o FinTracking →
      </a>
    </Moldura>
  );

export const PaginaDeConvite: React.FC<{ token: string }> = ({ token }) => {
  const [convite, setConvite] = useState<Convite | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  /** Qual dos dois botões foi tocado: só ele se enche. */
  const [enviando, setEnviando] = useState<'aceitar' | 'recusar' | null>(null);
  const [decidido, setDecidido] = useState<'aceito' | 'recusado' | null>(null);

  useEffect(() => {
    let vivo = true;
    buscar(`${API}/convite/${token}`)
      .then(async r => {
        const corpo = await r.json().catch(() => ({}));
        if (!vivo) return;
        if (!r.ok) setErro(corpo.error || 'Não consegui abrir este convite.');
        else setConvite(corpo);
      })
      .catch(() => vivo && setErro('Não consegui falar com o servidor. Tente de novo em alguns minutos.'));
    return () => { vivo = false; };
  }, [token]);

  const responder = useCallback(async (acao: 'aceitar' | 'recusar') => {
    setEnviando(acao);
    setErro(null);
    try {
      const r = await buscar(`${API}/convite/${token}/${acao}`, { method: 'POST' });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) setErro(corpo.error || 'Não consegui registrar a sua resposta.');
      else setDecidido(acao === 'aceitar' ? 'aceito' : 'recusado');
    } catch {
      setErro('Não consegui falar com o servidor. Tente de novo em alguns minutos.');
    } finally {
      setEnviando(null);
    }
  }, [token]);

  if (decidido === 'aceito') {
    return (
      <Recado
        tom="bom"
        titulo="Pronto!"
        texto={`Você vai receber o relatório de ${convite?.donoNome ?? 'quem te convidou'} todo mês,
                por email. Todo relatório traz um link para parar de receber, a qualquer momento.`}
      />
    );
  }
  if (decidido === 'recusado') {
    return (
      <Recado
        titulo="Convite recusado"
        texto={`Nenhum dado foi enviado, e ${convite?.donoNome ?? 'quem te convidou'} não pode
                te convidar de novo. Avisamos essa pessoa da sua resposta.`}
      />
    );
  }

  if (erro && !convite) return <Recado titulo="Convite indisponível" texto={erro} />;
  if (!convite) return <Carregando />;
  if (!convite.podeDecidir) {
    return <Recado titulo="Convite indisponível" texto={convite.motivo ?? 'Este convite não está mais válido.'} />;
  }

  return (
    <Moldura>
      <h2 className="convite-titulo">
        {convite.donoNome} quer que você acompanhe a vida financeira dela
      </h2>
      <p className="convite-texto">
        Todo mês o FinTracking manda para {convite.donoNome} um relatório do mês que
        passou. Ela pediu para ele chegar também em <strong>{convite.convidado}</strong>.
      </p>

      <div className="convite-escopo">
        <p className="convite-escopo-titulo">{convite.texto.titulo}</p>
        <p className="convite-escopo-item bom">✅ Você recebe {convite.texto.inclui}.</p>
        <p className="convite-escopo-item">🚫 Não inclui {convite.texto.naoInclui}.</p>
      </div>

      {erro && <p className="convite-erro">{erro}</p>}

      {/*
        Dois botões, mesmo tamanho, lado a lado. "Recusar" não é o link
        pequenininho embaixo: se dizer não é mais difícil que dizer sim, o sim
        não significa nada.
      */}
      <div className="convite-botoes">
        <button className="convite-botao recusar" disabled={enviando !== null} aria-busy={enviando === 'recusar'}
                onClick={() => responder('recusar')}>
          Não, obrigado
        </button>
        <button className="convite-botao aceitar" disabled={enviando !== null} aria-busy={enviando === 'aceitar'}
                onClick={() => responder('aceitar')}>
          Aceitar e receber
        </button>
      </div>

      <p className="convite-rodape">
        Convite enviado por {convite.donoNome} ({convite.donoEmail}), que digitou o seu
        endereço no FinTracking. Se você não conhece essa pessoa, é só recusar.
        Nenhum dado é enviado sem a sua resposta.
      </p>
    </Moldura>
  );
};

/**
 * A saída, e por que ela também PERGUNTA antes.
 *
 * Este link vive no rodapé de todo relatório, e clientes de email pré-carregam
 * links. Se o clique já removesse, o Gmail removeria a pessoa da lista sozinho,
 * sem ninguém tocar em nada — e o sintoma seria "parei de receber e não sei
 * por quê". Por isso a remoção sai de um POST, atrás de um botão.
 */
export const PaginaDeSaida: React.FC<{ token: string }> = ({ token }) => {
  const [saida, setSaida] = useState<Saida | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [pronto, setPronto] = useState(false);

  useEffect(() => {
    let vivo = true;
    buscar(`${API}/convite/sair/${token}`)
      .then(async r => {
        const corpo = await r.json().catch(() => ({}));
        if (!vivo) return;
        if (!r.ok) setErro(corpo.error || 'Não consegui abrir este link.');
        else setSaida(corpo);
      })
      .catch(() => vivo && setErro('Não consegui falar com o servidor.'));
    return () => { vivo = false; };
  }, [token]);

  const sair = useCallback(async () => {
    setEnviando(true);
    setErro(null);
    try {
      const r = await buscar(`${API}/convite/sair/${token}`, { method: 'POST' });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) setErro(corpo.error || 'Não consegui te tirar da lista.');
      else setPronto(true);
    } catch {
      setErro('Não consegui falar com o servidor.');
    } finally {
      setEnviando(false);
    }
  }, [token]);

  if (pronto) {
    return (
      <Recado
        tom="bom"
        titulo="Pronto, você saiu"
        texto={`Você não recebe mais o relatório de ${saida?.donoNome ?? 'quem te convidou'}.
                Avisamos essa pessoa, e ela não pode te convidar de novo.`}
      />
    );
  }
  if (erro && !saida) return <Recado titulo="Link indisponível" texto={erro} />;
  if (!saida) return <Carregando />;
  if (!saida.podeSair) {
    return <Recado titulo="Nada a fazer" texto={saida.motivo ?? 'Você já não recebe este relatório.'} />;
  }

  return (
    <Moldura>
      <h2 className="convite-titulo">Parar de receber?</h2>
      <p className="convite-texto">
        Você recebe hoje o relatório mensal de <strong>{saida.donoNome}</strong> em{' '}
        <strong>{saida.convidado}</strong>. Confirmando, os próximos não chegam mais.
      </p>
      <p className="convite-texto miudo">
        {saida.donoNome} é avisada, e não vai poder te convidar de novo.
      </p>

      {erro && <p className="convite-erro">{erro}</p>}

      {/*
        Aqui o peso NÃO é igual, e é o oposto do convite.
        
        No convite, aceitar e recusar dividem a tela porque a pessoa ainda não
        decidiu — ela abriu para decidir. Nesta página ela JÁ decidiu: clicou
        em "parar de receber" no rodapé do relatório e veio confirmar. Deixar
        "continuar recebendo" como o botão bonito seria empurrá-la de volta
        para o que ela acabou de escolher sair — o mesmo padrão escuro do
        convite, só que virado do avesso.
        
        Então o botão da ação dela é o firme, e o de voltar atrás é o discreto.
      */}
      <div className="convite-botoes">
        <button className="convite-botao sair" disabled={enviando} aria-busy={enviando} onClick={sair}>
          Sim, parar de receber
        </button>
        <a className="convite-botao voltar" href="https://fintracking.com.br">
          Continuar recebendo
        </a>
      </div>
    </Moldura>
  );
};

/* ── O convite para uma META COMPARTILHADA ──────────────────────────────── */

/**
 * A meta, vista por quem ainda não tem conta.
 *
 * ── Por que esta tela é diferente das duas acima ─────────────────────────
 *
 * As outras duas se resolvem sem login: o convite do relatório é aceito ou
 * recusado por um POST com o token, e pronto. Aqui não dá — a membresia é uma
 * linha com `user_id`, e é ela que vai receber os aportes. Entrar precisa de
 * conta.
 *
 * Então a tela tem duas metades e a ordem delas é a decisão: **ver primeiro,
 * cadastrar depois**. Cobrar o cadastro antes de mostrar no que a pessoa está
 * sendo chamada é exatamente o que `REGRAS_DA_META.exigeConta = false` existe
 * para não fazer — e é o que esvaziaria a feature sem ninguém reclamar, porque
 * não se reclama de um convite que não virou nada.
 *
 * ── O que ela mostra, e o que não mostra ─────────────────────────────────
 *
 * Nome da meta, alvo, quanto já juntou, e quem chamou. NÃO mostra quem pôs
 * quanto: isso é dado de membro, e quem está decidindo ainda não é membro.
 * A rota do servidor também não devolve — a garantia é lá, não aqui.
 */
interface ConviteDeMetaPublico {
  nome: string; alvo: number; total: number; de: string; email: string;
}

export const PaginaDeConviteDeMeta: React.FC<{
  token: string;
  /** `null` quando ainda não há sessão — a pessoa precisa se cadastrar. */
  temSessao: boolean;
  entrarComGoogle: () => Promise<void>;
  /**
   * Os campos de email e senha, para quem não entra pelo Google (05/10).
   *
   * Vem PRONTO de quem chama, e não importado aqui: o componente usa a sessão
   * (`useAuth`), e esta tela é desenhada só com props para poder ser testada
   * sem provedor nenhum. Ausente = só o Google, como era.
   */
  entradaComSenha?: React.ReactNode;
  /** Entra na meta com a sessão que já existe. */
  aceitar: () => Promise<void>;
  /** Some com o convite e leva ao app. */
  dispensar: () => void;
  /**
   * O convite existe, mas o servidor recusou abri-lo: expirado, cancelado, já
   * respondido, ou token que nunca valeu.
   *
   * Quem chama usa isto para ESQUECER o convite — e sem isso a pessoa fica
   * trancada fora do app. Ver a nota no `useEffect`.
   */
  aoNaoAbrir: () => void;
}> = ({ token, temSessao, entrarComGoogle, entradaComSenha, aceitar, dispensar, aoNaoAbrir }) => {
  const [convite, setConvite] = useState<ConviteDeMetaPublico | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  /*
   * ── O convite que não abre precisa ser ESQUECIDO ─────────────────────────
   *
   * O token fica guardado para atravessar o login (ver `utils/conviteDeMeta`),
   * e é ele que faz o `index.tsx` desviar para esta tela. Enquanto ele estiver
   * lá, TODA visita à raiz do app cai aqui.
   *
   * Quando o convite abre, tudo bem: há "Entrar" e "Agora não", e os dois
   * limpam. Quando ele NÃO abre, a tela é um recado sem botão — e a pessoa
   * fica trancada fora do app naquela aba, para sempre. Foi encontrado em
   * produção, abrindo um link com token inválido e depois indo para a raiz.
   *
   * ── E por que só quando o SERVIDOR respondeu ─────────────────────────────
   *
   * `!r.ok` é uma resposta: expirado, cancelado, já respondido, token inválido.
   * Nenhum deles vai virar aceitável depois, então esquecer é seguro.
   *
   * O `.catch` é outra coisa — rede caída, servidor dormindo no Render. Ali o
   * convite pode estar perfeitamente vivo, e esquecer o token perderia um
   * convite bom por causa de um minuto ruim. Esse fica.
   */
  useEffect(() => {
    let vivo = true;
    buscar(`${API}/convite-meta/${token}`)
      .then(async r => {
        const corpo = await r.json().catch(() => ({}));
        if (!vivo) return;
        if (!r.ok) {
          setErro(corpo.error || 'Não consegui abrir este convite.');
          aoNaoAbrir();
        } else setConvite(corpo);
      })
      .catch(() => vivo && setErro('Não consegui falar com o servidor. Tente de novo em alguns minutos.'));
    return () => { vivo = false; };
  }, [token, aoNaoAbrir]);

  const entrar = useCallback(async () => {
    setEnviando(true);
    setErro(null);
    try {
      /*
       * Sem sessão, a ida ao Google NÃO volta para esta URL — `redirectTo` é
       * `window.location.origin`, só o domínio. Quem guarda o token para a
       * volta é `utils/conviteDeMeta.ts`, e é `index.tsx` que traz a pessoa de
       * volta para cá depois do login.
       */
      if (!temSessao) return await entrarComGoogle();
      await aceitar();
    } catch (e: any) {
      setErro(e?.response?.data?.error ?? 'Não consegui te colocar na meta.');
      setEnviando(false);
    }
  }, [temSessao, entrarComGoogle, aceitar]);

  if (erro && !convite) return <Recado titulo="Convite indisponível" texto={erro} />;
  if (!convite) return <Carregando />;

  const falta = Math.max(convite.alvo - convite.total, 0);

  return (
    <Moldura>
      <h2 className="convite-titulo">
        {convite.de} te chamou para juntar dinheiro
      </h2>
      <p className="convite-texto">
        A meta é <strong>{convite.nome}</strong>. Já juntaram{' '}
        <strong>{formatBRL(convite.total, SEM_CENTAVOS)}</strong> de {formatBRL(convite.alvo, SEM_CENTAVOS)}
        {falta > 0 ? <>. Faltam {formatBRL(falta, SEM_CENTAVOS)}.</> : <>. Chegaram lá!</>}
      </p>

      {/*
        O que ela mais precisa saber antes de decidir, e o que a distingue do
        convite do relatório: aqui NADA do dinheiro dela é aberto. Sem esta
        frase o convite seria lido como o outro, e o "não" viria por um medo
        que não corresponde ao que está sendo pedido.
      */}
      <div className="convite-escopo">
        <p className="convite-escopo-titulo">O que entra e o que não entra</p>
        <p className="convite-escopo-item bom">
          ✅ Você vê quanto o grupo já juntou, e guarda o quanto quiser.
        </p>
        <p className="convite-escopo-item">
          🚫 Ninguém vê os seus gastos, o seu saldo, nem nada da sua conta.
        </p>
      </div>

      {erro && <p className="convite-erro">{erro}</p>}

      {/*
        Os dois botões com o mesmo peso — a mesma decisão do convite do
        relatório. Se dizer não é mais difícil que dizer sim, o sim não
        significa nada.
      */}
      <div className="convite-botoes">
        <button className="convite-botao recusar" disabled={enviando} onClick={dispensar}>
          Agora não
        </button>
        <button className="convite-botao aceitar" disabled={enviando} aria-busy={enviando} onClick={entrar}>
          {temSessao ? 'Entrar na meta' : 'Continuar com o Google'}
        </button>
      </div>

      {/*
        A segunda forma de entrar, só para quem ainda não tem sessão.

        O botão de cima dizia "Criar conta e entrar" e abria o Google — com a
        entrada por senha logo abaixo, que também cria conta, o rótulo passou a
        dizer o que o botão FAZ. Depois de entrar por aqui a sessão aparece, e
        o mesmo botão vira "Entrar na meta".
      */}
      {!temSessao && entradaComSenha && (
        <div className="convite-senha">
          <div className="login-ou" aria-hidden="true"><span>ou</span></div>
          {entradaComSenha}
        </div>
      )}

      <p className="convite-rodape">
        Convite enviado por {convite.de}, que digitou {convite.email} no FinTracking.
        Se você não conhece essa pessoa, é só fechar esta página.
      </p>
    </Moldura>
  );
};
